import { useEffect, useRef, useState } from 'react';
import { BrandMark } from './BrandMark';
import {
  Moon,
  Sun,
  LogOut,
  Plus,
  TriangleAlert,
  LayoutGrid,
  ChevronDown,
  LifeBuoy,
  CircleCheckBig,
  AlertOctagon,
  GitPullRequest,
  Megaphone,
  Menu,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAuth } from '../../lib/AuthContext';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './NotificationBell';

export function TopBar({ onMenu }: { onMenu?: () => void }) {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  /**
   * The reference's persistent Create button (Kai): raise anything from
   * anywhere.
   *
   * Every entry now does the SAME thing — navigate to a create SUBPAGE. Kai,
   * 2026-09-08:
   * *"the create button in the constant header should open the right page
   * with the create bit already open, that might be better than the popup,
   * either way behaviour needs to be consistent."* It was not: Ticket and Task
   * opened a modal over whatever you were looking at, while Problem and
   * Change navigated to a list that had no create button on it.
   *
   * Then: *"I think they should be subpages, not popups."* Which also
   * retired a real bug — a modal had to ask "discard?" itself because the
   * router could not see it close, so closing one after a URL change asked
   * twice (*"there's a double discard"*). A page only navigates, and
   * navigation is exactly what the one router-level guard intercepts.
   */
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (createRef.current && !createRef.current.contains(e.target as Node)) setCreateOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try {
      localStorage.setItem('theme', dark ? 'dark' : 'light');
    } catch {
      /* private windows throw; the toggle still works for this tab */
    }
  }, [dark]);

  return (
    <header className="safe-header safe-x flex shrink-0 items-center gap-2 border-b bg-card px-3 sm:gap-3 sm:px-4">
      {/* Step 141 — opens the sidebar drawer on phones; gone from md up where
          the rail is always there. */}
      {/* Step 189 — a full 44px tap target with a 24px glyph; 40px still read
          as "quite little" (Kai). `-ml-1` pulls it back to the true edge so it
          does not sit marooned in from the corner. */}
      <Button
        variant="ghost"
        size="icon"
        className="-ml-1 h-11 w-11 md:hidden"
        aria-label="Open menu"
        onClick={onMenu}
      >
        <Menu className="h-6 w-6" />
      </Button>
      {/* Step 188 — a compact brand on a phone, where the sidebar is a hidden
          drawer. It was the app NAME truncated to "Str…", which says nothing and
          crowded the hamburger. On md+ the sidebar carries the brand, so
          nothing here. */}
      <div className="flex min-w-0 items-center md:hidden">
        <BrandMark collapsed />
      </div>
      <div className="flex flex-1 justify-center sm:px-4">
        <GlobalSearch />
      </div>
      {/* Step 189 — mobile: the inline box was a sliver you could not read what
          you typed in (Kai). A tap opens the full search page, which has a
          real input and room for results. Desktop keeps the quick box. */}
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 sm:hidden"
        aria-label="Search"
        onClick={() => navigate('/search')}
      >
        <Search className="h-5 w-5" />
      </Button>
      <div ref={createRef} className="relative">
        <Button size="sm" onClick={() => setCreateOpen((o) => !o)}>
          <Plus /> <span className="hidden sm:inline">Create</span> <ChevronDown className="h-3.5 w-3.5" />
        </Button>
        {/**
         * Step 174 — Kai: "some menus spill outside and you have to scroll
         * sideways."
         *
         * A right-anchored panel with a fixed width sits `width` to the LEFT
         * of its button, and the button is inset from the edge of the screen —
         * so on a phone the panel's left edge lands off screen and the page
         * grows a horizontal scrollbar. A max-width does not help: capping the
         * width does not move the anchor. Below `sm` these are pinned to the
         * viewport instead; from `sm` up the original geometry is untouched.
         * The same fix as the notification bell, for the same reason.
         */}
        {createOpen && (
          <div className="fixed inset-x-2 top-14 z-40 max-h-[70vh] overflow-y-auto rounded-md border bg-popover py-1 shadow-md sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:max-h-none sm:w-64 sm:overflow-hidden">
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/tickets/new');
              }}
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>
                <span className="block text-sm font-medium">Ticket</span>
                <span className="block text-xs text-muted-foreground">Report an issue</span>
              </span>
            </button>
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/catalogue');
              }}
            >
              <LayoutGrid className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span>
                <span className="block text-sm font-medium">Request</span>
                <span className="block text-xs text-muted-foreground">Request a service</span>
              </span>
            </button>
            {/* Step 46 — a task is a ticket type, so it belongs beside the
                other two rather than buried on a ticket. Kai: *"someone
                should be able to create a task ticket to do something
                unrelated to a ticket."* */}
            {/* Step 48 — raising a problem is a deliberate act, so it sits
                beside the others rather than being reachable only by
                converting an incident. */}
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/problems/new');
              }}
            >
              <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
              <span>
                <span className="block text-sm font-medium">Problem</span>
                <span className="block text-xs text-muted-foreground">Investigate a recurring cause</span>
              </span>
            </button>
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/changes/new');
              }}
            >
              <GitPullRequest className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
              <span>
                <span className="block text-sm font-medium">Change</span>
                <span className="block text-xs text-muted-foreground">Plan work on a live service</span>
              </span>
            </button>
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/tasks/new');
              }}
            >
              <CircleCheckBig className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span>
                <span className="block text-sm font-medium">Task</span>
                <span className="block text-xs text-muted-foreground">Ask someone to do something</span>
              </span>
            </button>
            {/* Step 54 — declaring one from cold, when the outage is noticed
                before anybody has raised a ticket for it. Promoting an
                existing incident is the other door, on the ticket itself. */}
            <button
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/major-incidents/new');
              }}
            >
              <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <span>
                <span className="block text-sm font-medium">Major incident</span>
                <span className="block text-xs text-muted-foreground">Declare an outage</span>
              </span>
            </button>
            {/* Step 51 — Kai: announcements are raised from the Create menu
                "rather than being an admin thing". `?new=1` opens the side
                panel on arrival. */}
            <button
              className="flex w-full items-start gap-2.5 border-t px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                setCreateOpen(false);
                navigate('/announcements?new=1');
              }}
            >
              <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>
                <span className="block text-sm font-medium">Announcement</span>
                <span className="block text-xs text-muted-foreground">Tell everyone at once</span>
              </span>
            </button>
          </div>
        )}
      </div>
      {/* Step 88 — staff use the portal as themselves: they raise IT requests
          and read the same help articles. A plain link, because that is all
          it is; the session does not change. */}
      <Button
        variant="ghost"
        size="icon"
        aria-label="View the support portal"
        title="View the support portal"
        onClick={() => navigate('/portal')}
      >
        <LifeBuoy className="h-4 w-4" />
      </Button>
      {/* Step 125 — the bell. Beside the portal link and the theme toggle,
          left of the account block, which is where every product this desk
          already uses puts it. */}
      <NotificationBell />
      <Button variant="ghost" size="icon" aria-label="Toggle theme" onClick={() => setDark((d) => !d)}>
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      {me && (
        <div className="flex items-center gap-2 border-l pl-3">
          {/* Step 174 — Kai: "agents should be able to click their profile."
              The avatar and the name were decoration; they are the obvious
              thing to click and now they go somewhere. */}
          <button
            className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => navigate('/profile')}
            title="My profile"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/15 text-xs font-semibold text-brand">
              {(me.user.name ?? me.user.email).slice(0, 1).toUpperCase()}
            </span>
            <span className="hidden max-w-40 truncate text-xs text-muted-foreground md:block">
              {me.user.name ?? me.user.email}
            </span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={async () => {
              await signOut();
              navigate('/login', { replace: true });
            }}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      )}
    </header>
  );
}
